import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Droplets, ArrowLeft } from 'lucide-react';

const PoliticaCookies = () => {
  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Política de Cookies | Aqua Nexia</title>
        <meta name="description" content="Política de cookies de Aqua Nexia - Información sobre el uso de cookies en nuestro sitio web." />
      </Helmet>

      <header className="border-b bg-white sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-sky-600 flex items-center justify-center">
              <Droplets className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-lg text-slate-900">Aqua Nexia</span>
          </Link>
          <Link to="/" className="text-sm text-sky-600 hover:text-sky-700 flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" />
            Volver al inicio
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-3xl font-bold text-slate-900 mb-8">Política de Cookies</h1>
        <p className="text-sm text-slate-500 mb-8">Última actualización: Febrero 2026</p>

        <div className="prose prose-slate max-w-none space-y-6 text-slate-700 leading-relaxed">
          <section>
            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-3">1. ¿Qué son las cookies?</h2>
            <p>
              Las cookies son pequeños archivos de texto que se almacenan en el dispositivo del usuario al visitar 
              un sitio web. Permiten que el sitio recuerde información sobre la visita, como las preferencias 
              del usuario, datos de sesión y otros ajustes, facilitando así una navegación más eficiente.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-3">2. Cookies que utilizamos</h2>
            
            <h3 className="text-lg font-medium text-slate-800 mt-6 mb-2">2.1 Cookies técnicas (necesarias)</h3>
            <p>Son imprescindibles para el funcionamiento del sitio web y no requieren consentimiento:</p>
            <div className="overflow-x-auto mt-3">
              <table className="min-w-full border border-slate-200 text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="border border-slate-200 px-4 py-2 text-left font-semibold">Cookie</th>
                    <th className="border border-slate-200 px-4 py-2 text-left font-semibold">Finalidad</th>
                    <th className="border border-slate-200 px-4 py-2 text-left font-semibold">Duración</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-slate-200 px-4 py-2">auth_token</td>
                    <td className="border border-slate-200 px-4 py-2">Mantener la sesión del usuario autenticado</td>
                    <td className="border border-slate-200 px-4 py-2">Sesión / 7 días</td>
                  </tr>
                  <tr>
                    <td className="border border-slate-200 px-4 py-2">cookie_consent</td>
                    <td className="border border-slate-200 px-4 py-2">Almacenar la preferencia de cookies del usuario</td>
                    <td className="border border-slate-200 px-4 py-2">365 días</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h3 className="text-lg font-medium text-slate-800 mt-6 mb-2">2.2 Cookies funcionales</h3>
            <p>Permiten recordar las preferencias del usuario para mejorar la experiencia:</p>
            <div className="overflow-x-auto mt-3">
              <table className="min-w-full border border-slate-200 text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="border border-slate-200 px-4 py-2 text-left font-semibold">Cookie</th>
                    <th className="border border-slate-200 px-4 py-2 text-left font-semibold">Finalidad</th>
                    <th className="border border-slate-200 px-4 py-2 text-left font-semibold">Duración</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-slate-200 px-4 py-2">sidebar_state</td>
                    <td className="border border-slate-200 px-4 py-2">Recordar el estado del menú lateral</td>
                    <td className="border border-slate-200 px-4 py-2">30 días</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h3 className="text-lg font-medium text-slate-800 mt-6 mb-2">2.3 Cookies de terceros</h3>
            <p>Utilizadas por servicios externos necesarios para el funcionamiento de la plataforma:</p>
            <div className="overflow-x-auto mt-3">
              <table className="min-w-full border border-slate-200 text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="border border-slate-200 px-4 py-2 text-left font-semibold">Proveedor</th>
                    <th className="border border-slate-200 px-4 py-2 text-left font-semibold">Finalidad</th>
                    <th className="border border-slate-200 px-4 py-2 text-left font-semibold">Más información</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-slate-200 px-4 py-2">Stripe</td>
                    <td className="border border-slate-200 px-4 py-2">Procesamiento seguro de pagos</td>
                    <td className="border border-slate-200 px-4 py-2">
                      <a href="https://stripe.com/privacy" className="text-sky-600 hover:underline" target="_blank" rel="noopener noreferrer">
                        Política de Stripe
                      </a>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-3">3. Gestión de cookies</h2>
            <p>
              Al acceder al Sitio Web por primera vez, se mostrará un banner informativo sobre el uso de cookies. 
              El usuario puede aceptar o rechazar las cookies no esenciales.
            </p>
            <p className="mt-3">
              Además, el usuario puede configurar su navegador para bloquear o eliminar cookies. A continuación, 
              se incluyen enlaces a las instrucciones de los navegadores más habituales:
            </p>
            <ul className="list-disc pl-6 space-y-1 mt-3">
              <li><a href="https://support.google.com/chrome/answer/95647" className="text-sky-600 hover:underline" target="_blank" rel="noopener noreferrer">Google Chrome</a></li>
              <li><a href="https://support.mozilla.org/es/kb/cookies-informacion-que-los-sitios-web-guardan-en-" className="text-sky-600 hover:underline" target="_blank" rel="noopener noreferrer">Mozilla Firefox</a></li>
              <li><a href="https://support.apple.com/es-es/guide/safari/sfri11471/" className="text-sky-600 hover:underline" target="_blank" rel="noopener noreferrer">Safari</a></li>
              <li><a href="https://support.microsoft.com/es-es/microsoft-edge/eliminar-las-cookies-en-microsoft-edge" className="text-sky-600 hover:underline" target="_blank" rel="noopener noreferrer">Microsoft Edge</a></li>
            </ul>
            <p className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
              <strong>Nota:</strong> La desactivación de las cookies técnicas puede afectar al correcto funcionamiento 
              de la plataforma, impidiendo el inicio de sesión y otras funcionalidades esenciales.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-3">4. Actualizaciones</h2>
            <p>
              Aqua Nexia podrá actualizar esta Política de Cookies para reflejar cambios en las cookies utilizadas 
              o en la normativa aplicable. Se recomienda revisar esta página periódicamente.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-3">5. Contacto</h2>
            <p>
              Para cualquier consulta sobre el uso de cookies, puede contactarnos en <strong>privacidad@aquanexia.com</strong>.
            </p>
          </section>
        </div>
      </main>

      <footer className="py-8 px-4 border-t mt-12">
        <div className="max-w-4xl mx-auto flex flex-wrap items-center justify-center gap-4 text-sm text-slate-500">
          <Link to="/aviso-legal" className="hover:text-sky-600">Aviso Legal</Link>
          <span>·</span>
          <Link to="/politica-privacidad" className="hover:text-sky-600">Privacidad</Link>
          <span>·</span>
          <Link to="/terminos-condiciones" className="hover:text-sky-600">Términos</Link>
          <span>·</span>
          <Link to="/politica-cookies" className="hover:text-sky-600">Cookies</Link>
        </div>
      </footer>
    </div>
  );
};

export default PoliticaCookies;
